/**
 * Reddit Advertising REST helpers (Story 19.10, Task 2)
 *
 * Helper functions for the Reddit Ads API v3 endpoints used during OAuth
 * post-callback validation:
 *
 *   - GET /api/v3/me                                  — authenticated user profile
 *   - GET /api/v3/me/businesses?page.size=100         — enumerate Businesses (cursor paged)
 *   - GET /api/v3/businesses/{id}/ad_accounts?...     — enumerate ad accounts per business
 *
 * Reddit uses a single credential per request (Bearer access token) plus the
 * MANDATORY `User-Agent` header — there is no Developer Token like Microsoft Ads.
 *
 * Note: The token endpoint lives on `www.reddit.com` (separate host, separate
 * quota); the data API lives on `ads-api.reddit.com`.
 *
 * @see https://ads-api.reddit.com/api/v3/openapi.json
 */

import { safeFetch, sanitizeErrorMessage } from "../safe-fetch";
import { RedditAdsApiCallError, parseRedditAdsApiError } from "./errors";

// ============================================================================
// Constants
// ============================================================================

export const REDDIT_ADS_API_BASE = "https://ads-api.reddit.com/api/v3";

const API_TIMEOUT_MS = 25_000;

/**
 * Defence-in-depth caps for business + ad-account discovery.
 *
 * Reddit's `/me/businesses` is cursor-paginated (no documented hard cap on
 * total Businesses per user), so we cap iteration at 20 Businesses with
 * `partialDiscovery: true` warning if exceeded — agencies with sprawling
 * holdco trees need to know when their first 20 don't cover all clients.
 */
export const REDDIT_MAX_BUSINESSES = 20;
export const REDDIT_MAX_AD_ACCOUNTS_PER_BUSINESS = 100;

const DEFAULT_PAGE_SIZE = 100;

// ============================================================================
// Types
// ============================================================================

export interface RedditAdsUserInfo {
  /** Reddit-side user id (their OpenAPI calls this `id`) */
  id: string;
  /** Reddit user id with namespace prefix (typically `^t2_…` for individual users; do NOT hard-assert the prefix) */
  redditUserId: string;
  /** Public reddit username */
  redditUsername: string;
  /** User email — best-effort, may be empty */
  email: string;
  firstname: string;
  lastname: string;
}

export interface RedditAdAccount {
  /** Reddit ad-account ID (short alphanumeric, e.g. `a2_abc123`) */
  id: string;
  /** Display name */
  name: string;
  /** ISO 4217 currency code, e.g. "USD" */
  currency: string;
  /** IANA timezone, e.g. "America/Los_Angeles" */
  timeZoneId: string;
  /** Account type, e.g. "MANAGED" / "SELF_SERVE" */
  type: string;
  /** Admin-approval status, e.g. "VALID" / "PENDING" / "TRUSTED" */
  adminApproval: string;
  /** Parent business id */
  businessId: string;
  /** Parent business name (joined client-side after flatten) */
  businessName: string;
}

export interface RedditBusiness {
  /** UUID */
  id: string;
  name: string;
  country: string;
  industry: string;
  agencyAffiliated: boolean;
  adAccounts: RedditAdAccount[];
}

export interface RedditBusinessesResult {
  businesses: RedditBusiness[];
  /** Flat list of every ad account across all businesses, sorted ascending by `id` */
  flatAdAccounts: RedditAdAccount[];
  /** True when MAX_BUSINESSES or MAX_AD_ACCOUNTS_PER_BUSINESS cap was hit */
  partialDiscovery: boolean;
  /** Total ad-account count across all enumerated businesses (BEFORE truncation) */
  totalAdAccountCount: number;
  /** Total business count across all enumerated pages (BEFORE truncation) */
  totalBusinessCount: number;
}

export interface RedditRateLimit {
  policy: string;
  remaining: number;
  resetSeconds: number;
}

// ============================================================================
// Header Builder
// ============================================================================

/**
 * Builds Reddit Ads API v3 REST headers.
 *
 * The `User-Agent` header is MANDATORY — Reddit explicitly throttles or
 * rejects requests with default user-agent strings. Reddit also uses User-Agent
 * as the per-app rate-limit key; sharing it across apps would pool quota.
 */
export function buildRedditHeaders(
  accessToken: string,
  userAgent: string
): Record<string, string> {
  if (!accessToken) {
    throw new Error("accessToken is required for Reddit Ads API calls");
  }
  if (!userAgent) {
    throw new Error("userAgent is required for Reddit Ads API calls");
  }
  return {
    Authorization: `Bearer ${accessToken}`,
    "User-Agent": userAgent,
    Accept: "application/json",
    "Content-Type": "application/json",
  };
}

// ============================================================================
// RFC 8941 rate-limit header parsing
// ============================================================================

/**
 * Parses Reddit's RFC 8941 structured rate-limit headers.
 *
 * Reddit migrated to standardized headers in the v3 OpenAPI spec. The legacy
 * `X-RateLimit-Remaining` / `X-RateLimit-Reset` / `X-RateLimit-Used` headers
 * are no longer authoritative.
 *
 * Format:
 *   `RateLimit-Policy: "policy-name";q=quota;w=window`
 *   `RateLimit:        "policy-name";r=remaining;t=time-until-reset`
 *
 * Returns null (not throws) on malformed input — degenerate headers shouldn't
 * crash the data path. The adapter's `safeFetch` retry layer handles the
 * Retry-After fallback when this returns null.
 *
 * **Integration plan:** this helper is implemented and tested in Story 19.10
 * but **not yet wired into the `safeFetch` retry path** — the actual data
 * fetch (`fetchMetrics`) lands in Story 19.11. 19.11's reporting/analytics
 * helpers will pass response.headers through this parser to drive backoff
 * calibration alongside the existing `Retry-After` honor logic. Story 19.10
 * ships the helper now so 19.11 has zero parser-stability risk on day 1.
 *
 * Defensive against:
 *   - empty / missing headers
 *   - missing required parameters (`r=` / `t=`)
 *   - non-numeric values
 *   - unquoted policy names (returns null — strict per RFC 8941 §4.4)
 *   - whitespace padding (RFC 8941 ignores OWS)
 *   - multi-policy lists (returns the FIRST policy — convention)
 *   - negative numbers (returns null — semantically nonsensical)
 *   - policy names with embedded spaces (returns null — RFC 8941 tchar only)
 */
export function parseRedditRateLimitHeaders(
  headers: Headers
): RedditRateLimit | null {
  const rateLimitHeader = headers.get("ratelimit") ?? headers.get("RateLimit");
  if (!rateLimitHeader || rateLimitHeader.trim().length === 0) {
    return null;
  }

  // For multi-policy lists (`"a";r=1;t=1, "b";r=2;t=2`), take the first one.
  // Reddit's docs only emit one policy at a time but the RFC allows lists.
  const firstPolicy = rateLimitHeader.split(",")[0]?.trim() ?? "";
  if (firstPolicy.length === 0) {
    return null;
  }

  // Split policy components on `;` — first component is the policy name (in
  // quotes), subsequent components are `key=value` parameters.
  const components = firstPolicy.split(";").map((s) => s.trim());
  const policyComponent = components[0];
  if (!policyComponent || policyComponent.length === 0) {
    return null;
  }

  // Strict per RFC 8941: the policy name MUST be a quoted string (sf-string).
  // Unquoted names are invalid; return null rather than guessing.
  if (
    !policyComponent.startsWith('"') ||
    !policyComponent.endsWith('"') ||
    policyComponent.length < 2
  ) {
    return null;
  }
  const policy = policyComponent.slice(1, -1);

  // Reject embedded quotes (a non-escaped `"` inside the policy name violates
  // sf-string and probably indicates a parser-evasion attempt).
  if (policy.includes('"')) {
    return null;
  }

  // RFC 8941 §3.3.4 sf-token rule: ALPHA / DIGIT / `!#$%&'*+-.^_`|~/:` only.
  // Reject anything containing whitespace (the test canary explicitly checks
  // "weird policy with spaces").
  if (/\s/.test(policy) || policy.length === 0) {
    return null;
  }

  let remaining: number | null = null;
  let resetSeconds: number | null = null;

  for (let i = 1; i < components.length; i++) {
    const param = components[i];
    if (!param) continue;
    const eqIdx = param.indexOf("=");
    if (eqIdx === -1) continue;
    const key = param.slice(0, eqIdx).trim().toLowerCase();
    const valueStr = param.slice(eqIdx + 1).trim();
    if (valueStr.length === 0) continue;
    const value = Number(valueStr);
    if (!Number.isFinite(value) || value < 0) {
      // Reject negative or non-numeric — semantically nonsensical for rate
      // counters. Returns null on the entire parse so callers fall through to
      // the Retry-After header.
      return null;
    }
    if (key === "r") remaining = value;
    else if (key === "t") resetSeconds = value;
  }

  if (remaining === null || resetSeconds === null) {
    return null;
  }

  return { policy, remaining, resetSeconds };
}

// ============================================================================
// Response shapes (raw Reddit envelopes)
// ============================================================================

interface RawMeResponse {
  data?: {
    id?: string;
    reddit_user_id?: string;
    reddit_username?: string;
    email?: string;
    firstname?: string;
    lastname?: string;
  };
}

interface RawBusiness {
  id?: string;
  name?: string;
  country?: string;
  industry?: string;
  agency_affiliated?: boolean;
}

interface RawBusinessesResponse {
  data?: RawBusiness[];
  pagination?: {
    next?: string | null;
  };
}

interface RawAdAccount {
  id?: string;
  name?: string;
  currency?: string;
  time_zone_id?: string;
  type?: string;
  admin_approval?: string;
  business_id?: string;
}

interface RawAdAccountsResponse {
  data?: RawAdAccount[];
  pagination?: {
    next?: string | null;
  };
}

// ============================================================================
// User account
// ============================================================================

/**
 * Calls GET /api/v3/me and normalises the response into a stable shape.
 *
 * Used immediately after token exchange to enrich structured logging and as
 * the canonical health-check endpoint. The actual ads-access decision is made
 * downstream in `fetchRedditAdsBusinesses` / `fetchRedditAdsAdAccountsByBusiness`.
 *
 * **Watch for 403 → AC #13 `api_not_enabled` mapping** — Reddit gates Ads API
 * access. The errors.ts module owns the 403→`api_not_enabled` decision.
 *
 * Round 2 Pre-mortem Failure B: do NOT hard-assert the `^t2_` prefix on
 * `redditUserId` — Reddit may add new namespace prefixes for institutional/
 * business accounts. The field is logged but never gates flow.
 */
export async function fetchRedditAdsMe(
  accessToken: string,
  userAgent: string
): Promise<RedditAdsUserInfo> {
  const headers = buildRedditHeaders(accessToken, userAgent);

  const result = await safeFetch(
    `${REDDIT_ADS_API_BASE}/me`,
    { method: "GET", headers },
    API_TIMEOUT_MS
  );

  if (!result.success) {
    throw buildRedditAdsApiError(result.error, "Reddit Ads /me fetch failed");
  }

  const response = result.data;

  let data: RawMeResponse;
  try {
    data = (await response.json()) as RawMeResponse;
  } catch {
    throw new Error("Invalid Reddit Ads /me response: not JSON");
  }

  const me = data.data ?? {};
  return {
    id: typeof me.id === "string" ? me.id : "",
    redditUserId: typeof me.reddit_user_id === "string" ? me.reddit_user_id : "",
    redditUsername:
      typeof me.reddit_username === "string" ? me.reddit_username : "",
    email: typeof me.email === "string" ? me.email : "",
    firstname: typeof me.firstname === "string" ? me.firstname : "",
    lastname: typeof me.lastname === "string" ? me.lastname : "",
  };
}

// ============================================================================
// Businesses + ad accounts
// ============================================================================

/**
 * Enumerates every Business this OAuth grant can access.
 *
 * Reddit's `/me/businesses` is cursor-paginated via `pagination.next` token
 * → `?page.token=…`. We follow pages defensively until either:
 *   - `pagination.next` is null/missing (natural end), OR
 *   - we've collected MAX_BUSINESSES entries (sets `partialDiscovery=true`).
 *
 * @internal exported for test mocking; `fetchRedditAdsBusinessesAndAdAccounts`
 *   is the canonical entry-point for the OAuth callback flow.
 */
export async function fetchRedditAdsBusinesses(
  accessToken: string,
  userAgent: string
): Promise<{ businesses: RawBusiness[]; partialDiscovery: boolean; totalCount: number }> {
  const headers = buildRedditHeaders(accessToken, userAgent);

  const collected: RawBusiness[] = [];
  let pageToken: string | undefined;
  let totalCount = 0;
  let partialDiscovery = false;

  // Iteration cap (defensive against malformed `pagination.next` infinite loop).
  // 200 pages × 100 per page = 20,000 entries headroom; we'll never get there
  // because of MAX_BUSINESSES, but the cap exists to prevent runaway loops.
  const MAX_PAGES = 200;
  for (let page = 0; page < MAX_PAGES; page++) {
    const url = new URL(`${REDDIT_ADS_API_BASE}/me/businesses`);
    url.searchParams.set("page.size", String(DEFAULT_PAGE_SIZE));
    if (pageToken) {
      url.searchParams.set("page.token", pageToken);
    }

    const result = await safeFetch(
      url.toString(),
      { method: "GET", headers },
      API_TIMEOUT_MS
    );

    if (!result.success) {
      throw buildRedditAdsApiError(
        result.error,
        "Reddit Ads /me/businesses fetch failed"
      );
    }

    let data: RawBusinessesResponse;
    try {
      data = (await result.data.json()) as RawBusinessesResponse;
    } catch {
      throw new Error(
        "Invalid Reddit Ads /me/businesses response: not JSON"
      );
    }

    const items = Array.isArray(data.data) ? data.data : [];
    totalCount += items.length;

    for (const item of items) {
      if (collected.length >= REDDIT_MAX_BUSINESSES) {
        partialDiscovery = true;
        // Continue counting items in remaining pages so totalCount honestly
        // reports the OAuth grant size for the "X of N" UI hint.
        continue;
      }
      collected.push(item);
    }

    const next = data.pagination?.next;
    if (!next || typeof next !== "string" || next.length === 0) {
      break;
    }
    pageToken = next;
  }

  return { businesses: collected, partialDiscovery, totalCount };
}

/**
 * Enumerates every ad account under a single Business.
 *
 * Cursor-paginated via `pagination.next` token. Caps at
 * MAX_AD_ACCOUNTS_PER_BUSINESS to bound per-business latency; sets
 * `partialDiscovery=true` if exceeded.
 *
 * @internal exported for test mocking
 */
export async function fetchRedditAdsAdAccountsByBusiness(
  accessToken: string,
  userAgent: string,
  businessId: string
): Promise<{ adAccounts: RawAdAccount[]; partialDiscovery: boolean; totalCount: number }> {
  const headers = buildRedditHeaders(accessToken, userAgent);

  const collected: RawAdAccount[] = [];
  let pageToken: string | undefined;
  let totalCount = 0;
  let partialDiscovery = false;

  const MAX_PAGES = 200;
  for (let page = 0; page < MAX_PAGES; page++) {
    const url = new URL(
      `${REDDIT_ADS_API_BASE}/businesses/${encodeURIComponent(businessId)}/ad_accounts`
    );
    url.searchParams.set("page.size", String(DEFAULT_PAGE_SIZE));
    if (pageToken) {
      url.searchParams.set("page.token", pageToken);
    }

    const result = await safeFetch(
      url.toString(),
      { method: "GET", headers },
      API_TIMEOUT_MS
    );

    if (!result.success) {
      throw buildRedditAdsApiError(
        result.error,
        `Reddit Ads /businesses/${businessId}/ad_accounts fetch failed`
      );
    }

    let data: RawAdAccountsResponse;
    try {
      data = (await result.data.json()) as RawAdAccountsResponse;
    } catch {
      throw new Error(
        "Invalid Reddit Ads /businesses/{id}/ad_accounts response: not JSON"
      );
    }

    const items = Array.isArray(data.data) ? data.data : [];
    totalCount += items.length;

    for (const item of items) {
      if (collected.length >= REDDIT_MAX_AD_ACCOUNTS_PER_BUSINESS) {
        partialDiscovery = true;
        continue;
      }
      collected.push(item);
    }

    const next = data.pagination?.next;
    if (!next || typeof next !== "string" || next.length === 0) {
      break;
    }
    pageToken = next;
  }

  return { adAccounts: collected, partialDiscovery, totalCount };
}

/**
 * Canonical OAuth-callback discovery: enumerate Businesses, then sequentially
 * (NOT in parallel — preserves rate quota fairness across concurrent users)
 * fetch ad accounts per Business, flatten the result, sort lex-asc by `id`.
 *
 * **Trade-off rationale (Round 5 First Principles Q4):** Reddit's per-app rate
 * quota is shared across all users. Fanning out 20 parallel requests would
 * burn 20 quota slots in ~200ms; the next user connecting in the same minute
 * could get 429-throttled by our own previous burst. Sequential trades latency-
 * per-user (worst-case ~4.4s for 20 Businesses × 200ms each) for fairness
 * across concurrent users. Worst-case 4.4s is acceptable at connect time
 * (compared to a multi-minute 429-recovery delay if we burst); the flow runs
 * once per re-auth, not on every page load.
 */
export async function fetchRedditAdsBusinessesAndAdAccounts(
  accessToken: string,
  userAgent: string
): Promise<RedditBusinessesResult> {
  const businessesPage = await fetchRedditAdsBusinesses(accessToken, userAgent);

  const businesses: RedditBusiness[] = [];
  const flatAdAccounts: RedditAdAccount[] = [];
  let totalAdAccountCount = 0;
  let partialAdAccounts = false;

  for (const rawBusiness of businessesPage.businesses) {
    const businessId =
      typeof rawBusiness.id === "string" ? rawBusiness.id : "";
    if (!businessId) continue;

    const businessName =
      typeof rawBusiness.name === "string" ? rawBusiness.name : "";

    const adAccountsPage = await fetchRedditAdsAdAccountsByBusiness(
      accessToken,
      userAgent,
      businessId
    );
    if (adAccountsPage.partialDiscovery) {
      partialAdAccounts = true;
    }
    totalAdAccountCount += adAccountsPage.totalCount;

    const adAccounts: RedditAdAccount[] = [];
    for (const rawAcct of adAccountsPage.adAccounts) {
      const aid = typeof rawAcct.id === "string" ? rawAcct.id : "";
      if (!aid) continue;
      const account: RedditAdAccount = {
        id: aid,
        name: typeof rawAcct.name === "string" ? rawAcct.name : "",
        currency: typeof rawAcct.currency === "string" ? rawAcct.currency : "",
        timeZoneId:
          typeof rawAcct.time_zone_id === "string"
            ? rawAcct.time_zone_id
            : "",
        type: typeof rawAcct.type === "string" ? rawAcct.type : "",
        adminApproval:
          typeof rawAcct.admin_approval === "string"
            ? rawAcct.admin_approval
            : "",
        businessId:
          typeof rawAcct.business_id === "string"
            ? rawAcct.business_id
            : businessId,
        businessName,
      };
      adAccounts.push(account);
      flatAdAccounts.push(account);
    }

    businesses.push({
      id: businessId,
      name: businessName,
      country:
        typeof rawBusiness.country === "string" ? rawBusiness.country : "",
      industry:
        typeof rawBusiness.industry === "string" ? rawBusiness.industry : "",
      agencyAffiliated: rawBusiness.agency_affiliated === true,
      adAccounts,
    });
  }

  // Sort lex-asc by id for deterministic primary selection across re-auths
  // regardless of API response ordering. Reddit ad-account IDs are short
  // alphanumeric (e.g. `a2_abc123`), localeCompare is stable.
  flatAdAccounts.sort((a, b) => a.id.localeCompare(b.id));

  return {
    businesses,
    flatAdAccounts,
    partialDiscovery: businessesPage.partialDiscovery || partialAdAccounts,
    totalAdAccountCount,
    totalBusinessCount: businessesPage.totalCount,
  };
}

// ============================================================================
// Internal: build a typed RedditAdsApiCallError from a safeFetch error
// ============================================================================

function buildRedditAdsApiError(
  safeFetchError: {
    message: string;
    providerMessage?: string;
    providerStatus?: number;
  },
  prefix: string
): RedditAdsApiCallError {
  let parsedBody: unknown = {};
  if (safeFetchError.providerMessage) {
    try {
      parsedBody = JSON.parse(safeFetchError.providerMessage);
    } catch {
      // not JSON — fall back to status-only mapping
    }
  }
  const parsed = parseRedditAdsApiError(
    parsedBody,
    safeFetchError.providerStatus
  );
  return new RedditAdsApiCallError(
    parsed ?? {
      type: "unknown",
      message: sanitizeErrorMessage(safeFetchError.message),
      status: safeFetchError.providerStatus ?? null,
      retryable: false,
    },
    prefix
  );
}

// ============================================================================
// safeFetchReddit — Story 19.11 Task 4: RFC 8941 RateLimit-driven retry wiring
// ============================================================================

/** Default base backoff for Reddit retries (1s → 2s → 4s). */
export const REDDIT_BASE_BACKOFF_MS = 1_000;
/** Maximum retry attempts on retryable errors (3 total: 1 initial + 2 retries). */
export const REDDIT_MAX_RETRIES = 2;

/**
 * Pluggable sleep — production uses `setTimeout`. Tests can swap it out via
 * `__setRedditSleepImplForTests` to assert the scheduled delay deterministically.
 */
let sleepImpl: (ms: number) => Promise<void> = (ms) =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Test hook: replace the sleep implementation. Returns a restore function
 * the test should call in its cleanup hook. Production code MUST NOT call
 * this — it exists so vitest can assert exact scheduled delays (Task 4.3).
 */
export function __setRedditSleepImplForTests(
  impl: (ms: number) => Promise<void>
): () => void {
  const previous = sleepImpl;
  sleepImpl = impl;
  return () => {
    sleepImpl = previous;
  };
}

/** Result of a successful `safeFetchReddit` call. */
export interface SafeFetchRedditOk {
  readonly response: Response;
  readonly rateLimit: RedditRateLimit | null;
  readonly attempts: number;
  /** Total milliseconds slept across retries (deterministic for tests). */
  readonly totalSleepMs: number;
}

/**
 * Reddit-specific fetch wrapper that wires Story 19.10's dormant
 * `parseRedditRateLimitHeaders` parser into the retry-decision (AC #12).
 *
 * Retry behaviour:
 *   - 429 / 5xx / network error → retry up to `REDDIT_MAX_RETRIES` times
 *   - On 429: wait `max(Retry-After, RateLimit.resetSeconds * 1000, expBackoff)`
 *   - On 5xx / network: wait `expBackoff` (1s → 2s → 4s)
 *   - Non-retryable errors throw `RedditAdsApiCallError` immediately
 *
 * Headers `Authorization`, `User-Agent`, `Accept: application/json`, and
 * `Content-Type: application/json` are stamped automatically. Caller-supplied
 * `init.headers` overrides those defaults.
 *
 * NOTE: On retry, both `Retry-After` AND the RFC 8941 `RateLimit: ...;t=N`
 * value are honoured — wires Story 19.10's dormant parser per AC #12.
 */
export async function safeFetchReddit(
  url: string,
  accessToken: string,
  userAgent: string,
  init: RequestInit = {},
  timeoutMs = API_TIMEOUT_MS,
  prefix = "Reddit Ads API call failed"
): Promise<SafeFetchRedditOk> {
  const baseHeaders = buildRedditHeaders(accessToken, userAgent);
  const mergedHeaders: Record<string, string> = {
    ...baseHeaders,
    ...((init.headers as Record<string, string> | undefined) ?? {}),
  };

  let attempt = 0;
  let totalSleepMs = 0;
  let lastError: RedditAdsApiCallError | null = null;

  while (attempt <= REDDIT_MAX_RETRIES) {
    attempt++;

    // Direct fetch (NOT safeFetch) so we retain access to response.headers on
    // 429 responses — required for RFC 8941 `RateLimit` header parsing. We
    // re-implement the timeout + sanitization layer here at the cost of some
    // duplication; the tradeoff is necessary for the AC #12 wiring.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    let response: Response;
    try {
      response = await fetch(url, {
        ...init,
        headers: mergedHeaders,
        signal: controller.signal,
      });
    } catch (err) {
      clearTimeout(timeoutId);
      // Network / timeout error
      const message =
        err instanceof DOMException && err.name === "AbortError"
          ? `Request timed out after ${timeoutMs}ms`
          : err instanceof Error
            ? err.message
            : String(err);
      lastError = new RedditAdsApiCallError(
        {
          type: "provider_unavailable",
          message: sanitizeErrorMessage(message),
          status: null,
          retryable: true,
        },
        prefix
      );
      if (attempt > REDDIT_MAX_RETRIES) break;
      const expBackoffMs = REDDIT_BASE_BACKOFF_MS * Math.pow(2, attempt - 1);
      totalSleepMs += expBackoffMs;
      await sleepImpl(expBackoffMs);
      continue;
    }

    clearTimeout(timeoutId);

    if (response.ok) {
      const rateLimit = parseRedditRateLimitHeaders(response.headers);
      return { response, rateLimit, attempts: attempt, totalSleepMs };
    }

    // Non-2xx response — parse the body + headers to decide retry strategy.
    const rateLimit = parseRedditRateLimitHeaders(response.headers);

    let providerMessage: string | undefined;
    try {
      const text = await response.text();
      if (text) {
        const sanitized = sanitizeErrorMessage(text);
        providerMessage =
          sanitized.length > 500 ? `${sanitized.slice(0, 500)}...` : sanitized;
      }
    } catch {
      providerMessage = undefined;
    }

    let parsedBody: unknown = {};
    if (providerMessage) {
      try {
        parsedBody = JSON.parse(providerMessage);
      } catch {
        // not JSON — fall back to status-only mapping
      }
    }
    const parsed = parseRedditAdsApiError(parsedBody, response.status);

    const typedError = new RedditAdsApiCallError(
      parsed ?? {
        type: "unknown",
        message: sanitizeErrorMessage(
          `HTTP ${response.status} ${response.statusText}`
        ),
        status: response.status,
        retryable: false,
      },
      prefix
    );

    lastError = typedError;

    if (!typedError.retryable || attempt > REDDIT_MAX_RETRIES) {
      throw typedError;
    }

    // Compute wait — RFC 8941 wiring is here.
    const expBackoffMs = REDDIT_BASE_BACKOFF_MS * Math.pow(2, attempt - 1);

    // Retry-After header (parsed defensively — supports seconds-int OR HTTP date)
    let retryAfterMs = 0;
    const retryAfterHeader = response.headers.get("Retry-After");
    if (retryAfterHeader) {
      const seconds = Number(retryAfterHeader);
      if (Number.isFinite(seconds) && seconds >= 0) {
        retryAfterMs = seconds * 1000;
      } else {
        const dateMs = Date.parse(retryAfterHeader);
        if (!Number.isNaN(dateMs)) {
          retryAfterMs = Math.max(0, dateMs - Date.now());
        }
      }
    }

    // RFC 8941 RateLimit header — `t=N` (seconds until reset).
    const rateLimitResetMs =
      rateLimit !== null ? rateLimit.resetSeconds * 1000 : 0;

    const waitMs = Math.max(retryAfterMs, rateLimitResetMs, expBackoffMs);
    totalSleepMs += waitMs;
    await sleepImpl(waitMs);
  }

  // Exhausted retries — throw the last error.
  if (lastError) throw lastError;
  // Defensive: should never reach here
  throw new RedditAdsApiCallError(
    {
      type: "unknown",
      message: "safeFetchReddit exhausted retries with no captured error",
      status: null,
      retryable: false,
    },
    prefix
  );
}
